import { cleanUrlOfRedirectParams } from '@/features/shared/utils/url'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useLayoutEffect } from 'react'
import { REDIRECT_URL_SESSION_KEY } from '@/features/shared/constants/analytics'
import { logWarn } from '@/features/shared/utils/logger'

export const useCleanUrlOfRedirectParams = () => {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Capture redirect_url BEFORE URL cleaning happens
  // useLayoutEffect runs synchronously after DOM mutations but before paint
  // This ensures we capture the parameter before it's removed from the URL
  // this hook runs on app start, and this useLayoutEffect only runs on mount, so we don't need to track
  // if we've already stored the redirect url, as we implicitly run this once
  useLayoutEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const redirectUrl = urlParams.get('redirect_url')
    if (redirectUrl) {
      // Store in sessionStorage for pageEvent to read
      window.sessionStorage.setItem(REDIRECT_URL_SESSION_KEY, JSON.stringify(redirectUrl))
    }
  }, [])

  // our retailer url helpers in route.ts depend on storeName (and other params) to be present
  // these values are often reliant on data fetching or context, and therefore might be unavailable
  // on certain pages (if link is constructed outside of retailer context) or data calls (featured promotions)
  // in these instances, we want to log when this happens, as we likely have a bug in our code
  useLayoutEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const redirectReason = urlParams.get('redirect_from_invalid_url')
    if (redirectReason) {
      logWarn('redirect from invalid constructed url', {
        redirectReason,
      })
    }
  }, [])

  useEffect(() => {
    if (!pathname || !searchParams) return

    // cleans the URL in two scenarios:
    // page load i.e. user loads the app on a page that results in a redirect
    // client navigation i.e. user clicks a link that results in a redirect
    const cleanedUrl = cleanUrlOfRedirectParams()

    if (cleanedUrl) {
      window.history.replaceState(
        { ...window.history.state, as: cleanedUrl, url: cleanedUrl },
        '',
        cleanedUrl,
      )
    }
  }, [pathname, searchParams])
}
